import { Box } from '@chakra-ui/react'
import { memo, type ReactNode } from 'react'
import type { TooltipContentProps } from 'recharts'
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  LabelList,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'

import { textStyleKeys, textStyles } from '@mangovoice/design-system/textStyles'

import type { ExtensionData } from '../types'
import { MIN_BAR_CHART_DOMAIN } from '../utils/reportingConstants'

interface ChartColors {
  xTickFill: string
  xStroke: string
  yTickFill: string
  yStroke: string
  gridStroke: string
  labelFill: string
}

interface ExtensionComparisonChartBatchProps {
  data: ExtensionData[]
  dataKey: 'totalCalls' | 'totalMinutes'
  startIndex: number
  barColors: string[]
  chartColors: ChartColors
  CustomTooltip: (props: TooltipContentProps) => ReactNode
  isLast: boolean
  isFirst: boolean
  maxValue: number
}

const ExtensionComparisonChartBatch = memo(
  ({
    data,
    dataKey,
    startIndex,
    barColors,
    chartColors,
    CustomTooltip,
    isLast,
    isFirst,
    maxValue,
  }: ExtensionComparisonChartBatchProps) => {
    const xAxisTextStyle = textStyles[textStyleKeys.Body3]
    const fontSizeString = Array.isArray(xAxisTextStyle.fontSize)
      ? xAxisTextStyle.fontSize[0]
      : xAxisTextStyle.fontSize
    const fontSize = parseFloat(fontSizeString)
    const fontFamily = xAxisTextStyle.fontFamily

    const getBarColor = (index: number): string => {
      return barColors[(startIndex + index) % barColors.length]
    }

    const heightPerItem = 50
    const bottomPadding = 30
    const topPadding = isFirst ? 10 : 0
    const chartHeight = data.length * heightPerItem + topPadding + bottomPadding

    return (
      <Box
        width="100%"
        height={`${chartHeight}px`}
        mt={isFirst ? '0' : '-30px'}
        pr="60px"
      >
        <ResponsiveContainer width="100%" height="100%">
          <BarChart
            data={data}
            layout="vertical"
            margin={{
              top: Math.max(topPadding, 0),
              right: 60,
              left: 0,
              bottom: bottomPadding,
            }}
          >
            <CartesianGrid
              strokeDasharray="3 3"
              stroke={chartColors.gridStroke}
            />
            <XAxis
              type="number"
              domain={[0, Math.max(maxValue, MIN_BAR_CHART_DOMAIN)]}
              stroke={chartColors.xStroke}
              tick={{
                fill: chartColors.xTickFill,
                fontFamily: fontFamily,
                style: { fontSize: `${fontSize}px` },
              }}
              tickFormatter={(value) => value.toLocaleString()}
              hide={!isLast}
              interval="preserveStartEnd"
            />
            <YAxis
              type="category"
              dataKey="displayLabel"
              stroke={chartColors.yStroke}
              tick={{
                fill: chartColors.yTickFill,
                fontFamily: fontFamily,
                style: { fontSize: `${fontSize}px` },
                textAnchor: 'start',
                dx: -140,
              }}
              width={220}
              interval={0}
            />
            <Tooltip content={CustomTooltip} cursor={{ fill: '#aaaaaa33' }} />
            <Bar dataKey={dataKey} radius={[0, 4, 4, 0]} barSize={38}>
              {data.map((entry, index) => (
                <Cell
                  key={`cell-${entry.extension}`}
                  fill={getBarColor(index)}
                />
              ))}
              <LabelList
                dataKey={dataKey}
                position="right"
                formatter={(value) =>
                  typeof value === 'number'
                    ? value.toLocaleString()
                    : String(value ?? '')
                }
                style={{
                  fill: chartColors.labelFill,
                  fontFamily,
                  fontSize: `${fontSize}px`,
                }}
              />
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </Box>
    )
  },
  (prevProps, nextProps) => {
    return (
      prevProps.data === nextProps.data &&
      prevProps.dataKey === nextProps.dataKey &&
      prevProps.maxValue === nextProps.maxValue &&
      prevProps.startIndex === nextProps.startIndex &&
      prevProps.isFirst === nextProps.isFirst &&
      prevProps.isLast === nextProps.isLast &&
      prevProps.chartColors === nextProps.chartColors &&
      prevProps.barColors === nextProps.barColors &&
      prevProps.CustomTooltip === nextProps.CustomTooltip
    )
  },
)

ExtensionComparisonChartBatch.displayName = 'ExtensionComparisonChartBatch'

export default ExtensionComparisonChartBatch
